import { apiUrl } from "./url";

export const loginApi = (email, password) =>
  fetch(`${apiUrl}/auth_user`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  }).then((res) => {
    if (!res.ok) throw new Error("Invalid credentials");
    return res.json();
  });

export const exchangeImpersonationTokenApi = (impersonationToken) =>
  fetch(`${apiUrl}/exchange_impersonation_token`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ impersonation_token: impersonationToken }),
  }).then(async (res) => {
    const contentType = res.headers.get("content-type") || "";
    const raw = await res.text();

    let data = null;
    if (contentType.includes("application/json")) {
      try {
        data = JSON.parse(raw);
      } catch (error) {
        throw new Error("Invalid JSON received from impersonation API");
      }
    }

    if (!data) {
      throw new Error("Impersonation API is not returning JSON. Deploy the latest konnectFBR_api changes on production.");
    }

    if (!res.ok || data?.statusCode === 400) {
      throw new Error(data?.message || "Invalid impersonation token");
    }

    if (!data?.response?.token) {
      throw new Error(data?.message || "Failed to login as client.");
    }

    return data;
  });

export const dashboard = async (setLoader) => {
  try {
    const token = localStorage.getItem("jwt_token"); // token stored from login
    setLoader(true);
    const res = await fetch(`${apiUrl}/home`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        token: token,
      },
    });

    if (!res.ok) {
      throw new Error("Issue Api");
    }

    const data = await res.json();
    setLoader(false);

    return data; // return parsed data
  } catch (error) {
    throw error;
  }
};
export const verifyAdminApproval = async (approvalCode) => {
  const token = localStorage.getItem("jwt_token");
  
  const res = await fetch(`${apiUrl}/admin_approval/${approvalCode}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      token: token,
    },
  });

  if (!res.ok) {
    throw new Error("Invalid Code");
  }

  return await res.json();
};

const getFbrAuthorizationHeader = (globalData) => ({
  Authorization: globalData?.is_prod_token
    ? `Bearer ${globalData?.token_prod}`
    : `Bearer ${globalData?.token_dev}`,
});

const fetchFbrReferenceApi = async (url, globalData) => {
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
      ...getFbrAuthorizationHeader(globalData),
    },
  });

  if (!response.ok) {
    throw new Error(`FBR reference API error: ${response.status}`);
  }

  const data = await response.json();
  return Array.isArray(data) ? data : data?.response || data?.data || [];
};

const formatFbrLookupDate = (date) => {
  if (!date) return "";
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return date;

  const day = String(parsed.getDate()).padStart(2, "0");
  const month = parsed.toLocaleString("en-US", { month: "short" });
  const year = parsed.getFullYear();
  return `${day}-${month}-${year}`;
};

export const getFbrProvinces = (globalData) =>
  fetchFbrReferenceApi("https://gw.fbr.gov.pk/pdi/v1/provinces", globalData);

export const getFbrInvoiceTypes = (globalData) =>
  fetchFbrReferenceApi("https://gw.fbr.gov.pk/pdi/v1/doctypecode", globalData);

export const getFbrHsCodes = (globalData) =>
  fetchFbrReferenceApi("https://gw.fbr.gov.pk/pdi/v1/itemdesccode", globalData);

export const getFbrUoms = (globalData) =>
  fetchFbrReferenceApi("https://gw.fbr.gov.pk/pdi/v1/uom", globalData);

export const getFbrTransactionTypes = (globalData) =>
  fetchFbrReferenceApi("https://gw.fbr.gov.pk/pdi/v1/transtypecode", globalData);

export const getFbrRates = ({ date, transTypeId, originationSupplier }, globalData) =>
  fetchFbrReferenceApi(
    `https://gw.fbr.gov.pk/pdi/v2/SaleTypeToRate?date=${encodeURIComponent(formatFbrLookupDate(date))}&transTypeId=${encodeURIComponent(transTypeId)}&originationSupplier=${encodeURIComponent(originationSupplier)}`,
    globalData
  );

export const getFbrSroSchedules = ({ rateId, date, originationSupplier }, globalData) =>
  fetchFbrReferenceApi(
    `https://gw.fbr.gov.pk/pdi/v1/SroSchedule?rate_id=${encodeURIComponent(rateId)}&date=${encodeURIComponent(formatFbrLookupDate(date))}&origination_supplier_csv=${encodeURIComponent(originationSupplier)}`,
    globalData
  );

export const getFbrSroItems = ({ date, sroId }, globalData) =>
  fetchFbrReferenceApi(
    `https://gw.fbr.gov.pk/pdi/v2/SROItem?date=${encodeURIComponent(date)}&sro_id=${encodeURIComponent(sroId)}`,
    globalData
  );

export const checkSubmittedInvoicesApi = async (invoiceNumbers, isProd) => {
  const token = localStorage.getItem("jwt_token");

  const res = await fetch(`${apiUrl}/check_submitted_invoices`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      token: token,
    },
    body: JSON.stringify({
      invoiceNumbers,
      is_prod: isProd ? 1 : 0,
    }),
  });

  const data = await res.json();

  if (!res.ok || data?.statusCode === 400) {
    throw new Error(data?.message || "Failed to check submitted invoices.");
  }

  return data?.response?.submittedInvoiceNumbers || [];
};

export const getInvoiceDesignApi = async () => {
  const token = localStorage.getItem("jwt_token");

  const res = await fetch(`${apiUrl}/invoice_design`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      token: token,
    },
  });

  const data = await res.json().catch(() => null);

  if (!res.ok || data?.statusCode === 400) {
    throw new Error(data?.message || "Failed to fetch invoice design.");
  }

  return data?.response?.design || data?.response || data?.design || data;
};

export const saveInvoiceDesignApi = async (design) => {
  const token = localStorage.getItem("jwt_token");

  const res = await fetch(`${apiUrl}/invoice_design`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      token: token,
    },
    body: JSON.stringify({ design }),
  });

  const data = await res.json().catch(() => null);

  if (!res.ok || data?.statusCode === 400) {
    throw new Error(data?.message || "Failed to save invoice design.");
  }

  return data?.response || data;
};

export const uploadInvoiceDesignAssetApi = async (assetType, file) => {
  const token = localStorage.getItem("jwt_token");
  const formData = new FormData();
  formData.append("file", file);

  const res = await fetch(`${apiUrl}/invoice_design/upload/${assetType}`, {
    method: "POST",
    headers: {
      token: token,
    },
    body: formData,
  });

  const data = await res.json().catch(() => null);

  if (!res.ok || data?.statusCode === 400) {
    throw new Error(data?.message || "Failed to upload invoice design asset.");
  }

  return data?.response || data;
};

export const saveInvoiceApprovalApi = async ({ invoice, validationResponse, isProd }) => {
  const token = localStorage.getItem("jwt_token");

  const res = await fetch(`${apiUrl}/save_invoice_approval`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      token,
    },
    body: JSON.stringify({
      data: invoice,
      validation_response: validationResponse || {},
      is_prod: isProd ? 1 : 0,
    }),
  });

  const data = await res.json().catch(() => null);
  if (!res.ok || data?.statusCode === 400) {
    throw new Error(data?.message || "Failed to save invoice for approval.");
  }
  return data?.response || data;
};

export const getInvoiceApprovalsApi = async () => {
  const token = localStorage.getItem("jwt_token");

  const res = await fetch(`${apiUrl}/invoice_approvals`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      token,
    },
  });

  const data = await res.json().catch(() => null);
  if (!res.ok || data?.statusCode === 400) {
    throw new Error(data?.message || "Failed to fetch invoice approvals.");
  }
  return data?.response?.result || [];
};

export const cancelInvoiceApprovalApi = async (approvalId, remarks) => {
  const token = localStorage.getItem("jwt_token");

  const res = await fetch(`${apiUrl}/cancel_invoice_approval`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      token,
    },
    body: JSON.stringify({ approval_id: approvalId, remarks }),
  });

  const data = await res.json().catch(() => null);
  if (!res.ok || data?.statusCode === 400) {
    throw new Error(data?.message || "Failed to cancel invoice approval.");
  }
  return data?.response || data;
};

export const markInvoiceApprovalSubmittedApi = async (approvalId) => {
  const token = localStorage.getItem("jwt_token");

  const res = await fetch(`${apiUrl}/mark_invoice_approval_submitted`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      token,
    },
    body: JSON.stringify({ approval_id: approvalId }),
  });

  const data = await res.json().catch(() => null);
  if (!res.ok || data?.statusCode === 400) {
    throw new Error(data?.message || "Failed to mark invoice approval submitted.");
  }
  return data?.response || data;
};

export const getBuyerSuggestionsApi = async (search) => {
  const token = localStorage.getItem("jwt_token");

  const res = await fetch(`${apiUrl}/buyer_suggestions`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      token: token,
    },
    body: JSON.stringify({ search }),
  });

  const data = await res.json();

  if (!res.ok || data?.statusCode === 400) {
    throw new Error(data?.message || "Failed to fetch buyer suggestions.");
  }

  return data?.response?.result || [];
};

export const processInvoiceApi = async (
  setLoader,
  invoiceData,
  route,
  globalData,
  options = {}
) => {
  setLoader(true);
  try {
    const token = localStorage.getItem("jwt_token");
    const timeoutMs = options.timeoutMs || 60000;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    const response = await fetch(route, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: globalData?.is_prod_token
            ? `Bearer ${globalData?.token_prod}`
            : `Bearer ${globalData?.token_dev}`,
        },
        body: JSON.stringify(invoiceData),
        signal: controller.signal,
      }).finally(() => clearTimeout(timeoutId));

    let result;
    try {
      result = await response.json();
    } catch {
      result = await response.text();
    }

    setLoader(false);
    if (
      result?.validationResponse?.status === "Valid" &&
      (route === "https://gw.fbr.gov.pk/di_data/v1/di/postinvoicedata" ||
        route === "https://gw.fbr.gov.pk/di_data/v1/di/postinvoicedata_sb")
    ) {
      console.log("resultant", result, route);

      let data = {
        ...invoiceData,
        fbrinvoicenumber: result?.invoiceNumber,

        items: invoiceData.items.map((item, index) => {
          const matchedStatus =
            result?.validationResponse?.invoiceStatuses?.find(
              (s) => s.itemSNo === String(index + 1)
            );

          return {
            ...item,
            invoiceNumber:
              matchedStatus?.invoiceNo ||
              `${result.invoiceNumber}-${index + 1}`,
          };
        }),
      };

      try {
        const response = await fetch(
          `${apiUrl}${
            globalData?.is_prod_token ?"/save_invoice_prod":"/save_invoice"
          }`,
        
        
        
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              token: `${token}`,
            },
            body: JSON.stringify({
              data: data,
            }),
          }
        );

        if (!response.ok) {
          throw new Error(`Server error: ${response.status}`);
        }
      } catch (error) {
        console.error("Error saving invoice:", error);
        throw error;
      }
    }
    

    return result; // ✅ always return parsed data (even in error case)
  } catch (error) {
    setLoader(false);
    console.error("Error in processInvoiceApi:", error);
    if (error?.name === "AbortError") {
      return { error: "FBR API timeout" };
    }
    return { error: error.message }; // return error object instead of throwing
  }
};

export const searchFilterApi = async (payload, setLoader, route) => {
  setLoader(true);
  try {
    const token = localStorage.getItem("jwt_token"); // token stored from login

    const response = await fetch(`${apiUrl}${route}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        token: token,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error("Issue with API response");
    }

    const result = await response.json();
    setLoader(false);
    return result; // return parsed data
  } catch (error) {
    console.error("Error in processInvoiceApi:", error);
    throw error;
  }
};

export const saveTokenApi = async (type, tokens) => {
  try {
    const token = localStorage.getItem("jwt_token"); // token stored from login
    let payload = {
      isprod: type === "sandbox" ? 0 : 1,
      token: tokens,
    };
    const response = await fetch(`${apiUrl}/save_token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        token: token,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error("Issue with API response");
    }

    const result = await response.json();
    return result; // return parsed data
  } catch (error) {
    console.error("Error in processInvoiceApi:", error);
    throw error;
  }
};
export const enableTokenApi = async (value, setLoader) => {
  try {
    const token = localStorage.getItem("jwt_token"); // token stored from login
    let payload = {
      is_prod_token: value,
    };
    const response = await fetch(`${apiUrl}/enable_token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        token: token,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error("Issue with API response");
    }

    const result = await response.json();

    setLoader(false);
    return result; // return parsed data
  } catch (error) {
    console.error("Error in processInvoiceApi:", error);
    throw error;
  }
};

// Example mock GET APIs (they return Promises with hardcoded demo data)
// Replace fetch() with your real backend URLs if you have an API server

export const getProvinces = async () => {
  try {
    const token = localStorage.getItem("jwt_token"); // token stored from login
    let payload = {
      isprod: "1",
    };
    const response = await fetch(`${apiUrl}/provinces`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        token: token,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error("Issue with API response");
    }
    const result = await response.json();
    console.log("provinces", result);

    return result?.response; // return parsed data
  } catch (error) {
    console.error("Error in processInvoiceApi:", error);
    throw error;
  }
};
export const getRegisterTypes = async () => {
  try {
    const token = localStorage.getItem("jwt_token"); // token stored from login
    let payload = {
      isprod: "1",
    };
    const response = await fetch(`${apiUrl}/Get_Reg_Type`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        token: token,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error("Issue with API response");
    }
    const result = await response.json();
    console.log("provinces", result);

    return result?.response; // return parsed data
  } catch (error) {
    console.error("Error in processInvoiceApi:", error);
    throw error;
  }
};

export const getInvoiceTypes = async () => {
  try {
    const token = localStorage.getItem("jwt_token"); // token stored from login
    let payload = {
      isprod: "1",
    };
    const response = await fetch(`${apiUrl}/doctypecode`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        token: token,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error("Issue with API response");
    }
    const result = await response.json();

    return result?.response; // return parsed data
  } catch (error) {
    console.error("Error in processInvoiceApi:", error);
    throw error;
  }
};
// export const getHsCodes=async () =>{

//   try {
//     const token = localStorage.getItem("jwt_token"); // token stored from login
// let payload={
//   "isprod":"1"
// }
//     const response = await fetch(`${apiUrl}/itemdesccode`, {
//       method: "POST",
//       headers: {
//         "Content-Type": "application/json",
//         "token": token,
//       },
//       body: JSON.stringify(payload),
//     });

//     if (!response.ok) {
//       throw new Error("Issue with API response");
//     }
//     const result = await response.json();

//     return result?.response; // return parsed data
//   } catch (error) {
//     console.error("Error in processInvoiceApi:", error);
//     throw error;
//   }
// }
export const getHsCodes = () =>
  Promise.resolve([
    {
      hS_CODE: "8432.1010",
      description:
        "NUCLEAR REACTOR, BOILERS, MACHINERY AND MECHANICAL APPLIANCES; PARTS THEREOF. - AGRICULTURAL, HORTICULTURAL OR FORESTRY MACHINERY FOR SOIL PREPARATION OR CULTI- VATION; LAWN OR SPORTS GROUND ROLLERS. - CHISEL PLOUGHS",
    },
    {
      hS_CODE: "0304.7400",
      description:
        "FISH AND CRUSTACEANS, MOLLUSCS AND OTHER AQUATIC INVERTEBRATES - FISH FILLETS AND OTHER FISH MEAT (WHETHER OR NOT MINCED), FRESH, CHILLED OR FROZEN. - HAKE (MERLUCCIUS SPP., UROPHYCIS SPP.)",
    },
  ]);

export const getUOMs = async () => {
  try {
    const token = localStorage.getItem("jwt_token"); // token stored from login
    let payload = {
      isprod: "1",
    };
    const response = await fetch(`${apiUrl}/uom`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        token: token,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error("Issue with API response");
    }
    const result = await response.json();

    return result?.response; // return parsed data
  } catch (error) {
    console.error("Error in processInvoiceApi:", error);
    throw error;
  }
};
export const getSaleTypes = async () => {
  try {
    const token = localStorage.getItem("jwt_token"); // token stored from login
    let payload = {
      isprod: "0",
    };
    const response = await fetch(`${apiUrl}/transtype_code`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        token: token,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error("Issue with API response");
    }
    const result = await response.json();

    return result?.response; // return parsed data
  } catch (error) {
    console.error("Error in processInvoiceApi:", error);
    throw error;
  }
};
